import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Role -> ordered walkthrough steps. Each step: title, body, optional cta {label,to}, optional target testid.
const STEPS = {
  visitor: [
    { title: "Welcome to Enhancing Your World", body: "This is your living pathway — Discover, Join, Explore, Participate, Practice, Support, Steward. Let's find your starting point.", cta: { label: "Start GAUGE Exploration", to: "/gauge" } },
    { title: "Explore offerings by need", body: "Begin with a need — Reiki, Float Therapy, Breathwork and more — then choose a center, an enhancer, and a time.", cta: { label: "Browse offerings", to: "/wellness" } },
    { title: "Find your nearest center", body: "Step into a flagship or affiliate center to see everything happening there.", cta: { label: "Browse centers", to: "/locations" } },
    { title: "Save your journey", body: "Become a Discovery Member (free) to save your GAUGE results and book sessions.", cta: { label: "Join free", to: "/register" } },
  ],
  discovery: [
    { title: "Complete your profile", body: "Add your details and choose exactly who can see each one. Personal contact defaults to private.", cta: { label: "Open Profile & Privacy", to: "/settings/profile" } },
    { title: "Sign your agreements", body: "Some bookings need a signed agreement. Review and sign them from your dashboard.", cta: { label: "View agreements", to: "/agreements" } },
    { title: "Save your GAUGE results", body: "Take GAUGE Exploration and save it to your journey to track your growth over time.", cta: { label: "Start GAUGE", to: "/gauge" } },
  ],
  community: [
    { title: "Join a group", body: "Find your people — Men's, Women's, Couples, Recovery, Stewardship and more spirals.", cta: { label: "Explore community", to: "/community" } },
    { title: "Use your Daily Coach", body: "Reflect on today's GAUGE prompt and get personalized recommendations.", cta: { label: "Open Daily Coach", to: "/coach" } },
    { title: "Complete your profile", body: "Control what's public or private and preview your profile as any role.", cta: { label: "Profile & Privacy", to: "/settings/profile" } },
  ],
  enhancer: [
    { title: "Apply to locations", body: "Request to offer your services at approved centers from your dashboard.", cta: { label: "Open dashboard", to: "/dashboard" } },
    { title: "Select approved services", body: "At EYW centers, choose official center services; for your own or virtual spaces, create custom offerings." },
    { title: "Set your schedule", body: "Manage availability, buffers, and your upcoming bookings." },
    { title: "Your public profile", body: "Choose which business and contact details appear publicly.", cta: { label: "Profile & Privacy", to: "/settings/profile" } },
  ],
  location_manager: [
    { title: "Review enhancer applications", body: "Accept, deny, or pause enhancers who want to offer at your center.", cta: { label: "Open dashboard", to: "/dashboard" } },
    { title: "Manage rooms & resources", body: "Set capacity, cleaning buffers, and connect rooms to offerings." },
    { title: "Publish location offerings", body: "Schedule enhancers and publish what's available at your center." },
  ],
  admin: [
    { title: "Review pending approvals", body: "Applications, venue approvals, and stewardship posts await your review.", cta: { label: "Open control room", to: "/admin" } },
    { title: "Agreements & bookings", body: "Manage agreements, offerings, and monitor bookings and revenue." },
    { title: "Fill the Help Center", body: "Auto-capture real screenshots and edit role guides under the Help Center tab.", cta: { label: "Help Center", to: "/help" } },
  ],
};

const ROLE_ALIASES = { staff: "admin", council: "admin", vendor: "enhancer", speaker: "enhancer", musician: "enhancer" };

export default function Walkthrough() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const role = user ? (ROLE_ALIASES[user.role] || user.role) : "visitor";
  const steps = STEPS[role] || STEPS.visitor;
  const key = `eyw_walkthrough_${role}_done`;

  useEffect(() => {
    if (!ready) return;
    if (localStorage.getItem(key) === "1") return;
    // guests: only auto-open on the homepage to avoid stacking with other prompts
    if (!user && loc.pathname !== "/") return;
    const t = setTimeout(() => { setI(0); setOpen(true); }, 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user, key]);

  // Allow other UI (e.g. Help Center) to restart the tour.
  useEffect(() => {
    const restart = () => { localStorage.removeItem(key); setI(0); setOpen(true); };
    window.addEventListener("eyw:restart-walkthrough", restart);
    return () => window.removeEventListener("eyw:restart-walkthrough", restart);
  }, [key]);

  const finish = useCallback(() => { localStorage.setItem(key, "1"); setOpen(false); }, [key]);

  if (!open || !steps.length) return null;
  const step = steps[i];
  const last = i === steps.length - 1;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[min(92vw,380px)] animate-in fade-in slide-in-from-bottom-4" data-testid="walkthrough">
      <div className="bg-[#144F75] text-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="flex items-center gap-2 text-[#D98E73]"><Compass size={16} /><span className="text-xs uppercase tracking-wider">Quick tour · {i + 1}/{steps.length}</span></div>
          <button onClick={finish} data-testid="walkthrough-close" aria-label="Close tour" className="h-10 w-10 -m-2 grid place-items-center text-white/70 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">
          <h3 className="font-serif text-xl">{step.title}</h3>
          <p className="text-white/85 text-sm mt-2">{step.body}</p>
          {step.cta && (
            <button onClick={() => { navigate(step.cta.to); }} data-testid="walkthrough-cta"
              className="mt-4 inline-flex items-center gap-1.5 bg-[#D98E73] hover:bg-[#c67a60] text-white rounded-full px-4 py-2 text-sm font-medium transition-colors">
              {step.cta.label} <ArrowRight size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 bg-white/5 border-t border-white/10">
          <button onClick={finish} data-testid="walkthrough-skip" className="text-white/60 hover:text-white text-xs">Don't show again</button>
          <div className="flex items-center gap-2">
            {i > 0 && <button onClick={() => setI(i - 1)} data-testid="walkthrough-back" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm"><ArrowLeft size={14} /> Back</button>}
            <button onClick={() => (last ? finish() : setI(i + 1))} data-testid="walkthrough-next" className="bg-white text-[#144F75] rounded-full px-4 py-1.5 text-sm font-medium">{last ? "Got it" : "Next"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
